// Microsoft Teams chats in the room-less chat pane (pages/Chat/Coversation.js; backend contract:
// Teams mirror groups, `group.source === 'teams'`). Pure helpers: the pane reads what a Teams chat
// allows from here and keeps its own UI. A native chat gets `null` from teamsChatFrom and every
// check is a no-op. Ported from the mobile app's components/message/teamsChat.js.
import {
  TEAMS_ID_PREFIX,
  TEAMS_UNSUPPORTED_ACTIONS,
  isTeamsPayload,
  isTeamsUserId,
  teamsInitials as coreTeamsInitials,
} from "./teamsCore";
import { teamsProjectOf } from "./teamsChats";

export { TEAMS_ID_PREFIX, isTeamsPayload };

export const TEAMS_UNSUPPORTED_DEFAULT = TEAMS_UNSUPPORTED_ACTIONS;

/** window event for a signed Teams file that failed to load (detail: { action, url, status, code }). */
export const TEAMS_FILE_ERROR_EVENT = "kjuup:teamsFileError";

/** The settings page the read-only bar's "Reconnect" opens. */
export const TEAMS_SETTINGS_PATH = "/profile-setting?section=teams";

const WRITABLE_ACCOUNT = ["connected", "connecting"];

/** True for a synthetic Teams person id ('teams:<aadUserId>'). Never a Kjuup user. */
export const isTeamsSenderId = isTeamsUserId;

/** True for a message row that came from (or went to) a Teams chat. */
export const isTeamsRow = (row) => !!row && (row.isTeams === true || row.source === "teams" || (row.teams != null && typeof row.teams === "object"));

/**
 * What the pane knows about a Teams chat, merged from the payloads it has, newest first
 * (/group/:id/info or getChat groupInfo, then the subscribe groupInfo, then the open-chat entry).
 * Returns null for a native chat.
 *   { chatType: 'oneOnOne'|'group', webUrl, canWrite, unsupported: string[], accountStatus, state, topic, project }
 */
export function teamsChatFrom(...sources) {
  const list = sources.filter((s) => s && typeof s === "object");
  if (!list.some(isTeamsPayload)) return null;
  const pick = (fn) => {
    for (const s of list) {
      const v = fn(s);
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };
  const nested = (s) => (s.group && typeof s.group === "object" ? s.group : null);
  const teamsOf = (s) => (s.teams && typeof s.teams === "object" ? s.teams : nested(s)?.teams && typeof nested(s).teams === "object" ? nested(s).teams : null);
  const unsupported = pick((s) => (Array.isArray(s.unsupportedActions) ? s.unsupportedActions : Array.isArray(nested(s)?.unsupportedActions) ? nested(s).unsupportedActions : undefined));
  const rawType = pick((s) => s.teamsChatType || nested(s)?.teamsChatType || teamsOf(s)?.chatType);
  const accountStatus = pick((s) => teamsOf(s)?.accountStatus);
  const state = pick((s) => teamsOf(s)?.state);
  const canWriteFlag = pick((s) => (typeof s.canWriteMessages === "boolean" ? s.canWriteMessages : typeof nested(s)?.canWriteMessages === "boolean" ? nested(s).canWriteMessages : undefined));
  return {
    chatType: rawType === "oneOnOne" ? "oneOnOne" : "group",
    webUrl: pick((s) => s.webUrl || nested(s)?.webUrl || teamsOf(s)?.webUrl) || null,
    canWrite: canWriteFlag !== false,
    unsupported: Array.isArray(unsupported) && unsupported.length ? unsupported.slice() : TEAMS_UNSUPPORTED_DEFAULT.slice(),
    accountStatus: accountStatus || null,
    state: state || null,
    topic: pick((s) => teamsOf(s)?.topic) || null,
    // The user's Microsoft account the chat belongs to (several can be connected), or null.
    accountEmail: pick((s) => teamsOf(s)?.accountEmail) || null,
    // The project room the chat is filed under (teamsChats.teamsProjectOf), or null.
    project: pick((s) => teamsProjectOf(s)) || null,
  };
}

/** True when `action` (an unsupportedActions value) must be hidden in this chat. Native: false. */
export function teamsHides(teams, action) {
  if (!teams) return false;
  const list = Array.isArray(teams.unsupported) && teams.unsupported.length ? teams.unsupported : TEAMS_UNSUPPORTED_DEFAULT;
  return list.includes(action) || TEAMS_UNSUPPORTED_DEFAULT.includes(action);
}

/**
 * Why a Teams chat is read-only, for the bar over the composer: 'reconnect' (the account is not
 * connected), 'left' (the owner left / lost the chat in Teams) or 'readOnly'. null while writable.
 */
export function teamsReadOnlyReason(teams, canWrite) {
  if (!teams) return null;
  const writable = canWrite !== undefined ? canWrite !== false : teams.canWrite !== false;
  if (writable) return null;
  if (teams.accountStatus && !WRITABLE_ACCOUNT.includes(teams.accountStatus)) return "reconnect";
  if (teams.state && teams.state !== "active") return "left";
  return "readOnly";
}

/** The read-only bar text for teamsReadOnlyReason(...). */
export function teamsReadOnlyText(reason, strings) {
  const tr = (k, f) => (strings && strings[k]) || f;
  if (reason === "reconnect") return tr("teams_read_only_reconnect", "Reconnect Microsoft Teams in the settings to send messages.");
  if (reason === "left") return tr("teams_read_only_left", "You are no longer a member of this Microsoft Teams chat.");
  return tr("teams_read_only", "This Microsoft Teams chat is read-only.");
}

/**
 * Delivery of an own message into Teams: 'failed' (teamsDeliveryFailed / sendState failed),
 * 'pending' (queued or sending media) or null (delivered, or not a Teams row). Teams has no receipts.
 */
export function teamsDeliveryOf(row) {
  if (!isTeamsRow(row)) return null;
  if (row.teamsDeliveryFailed) return "failed";
  const st = row.teams && row.teams.sendState;
  if (st === "failed") return "failed";
  if (st === "pending" || st === "sending") return "pending";
  return null;
}

/**
 * What one message of a Teams chat allows (its `teams` block from getChat / `created`): edit and
 * delete only when the backend says so (no 10-minute window in Teams), reply and react only once the
 * message reached Teams (not while it is still being sent, pending in Teams or failed). Native rows:
 * null.
 */
export function teamsRowCaps(row) {
  if (!isTeamsRow(row)) return null;
  const t = row.teams && typeof row.teams === "object" ? row.teams : null;
  const notSentYet = !!row.failedToSend || !!row.retryRequest || (!!row.local && !row.isSent) || teamsDeliveryOf(row) != null;
  const delivered = !notSentYet && (!t || !!t.graphMessageId || t.origin === "teams");
  return {
    canEdit: !!(t && t.canEdit) && !t.isPlaceholder && !(Number(t.part) > 0) && row.messageType === "simple" && !row.isDeleted,
    canDelete: !!(t && t.canDelete) && !row.isDeleted,
    canReply: delivered && !row.isDeleted,
    canReact: delivered && !row.isDeleted,
    isPlaceholder: !!(t && t.isPlaceholder),
  };
}

/** True when the owner may react to `row` now: native rows always, Teams rows once delivered. */
export function canReactToRow(row) {
  const caps = teamsRowCaps(row);
  return !caps || caps.canReact;
}

/**
 * Select mode in a Teams chat: delete only when every selected message may be deleted
 * (teams.canDelete, or a local message not sent yet).
 */
export function teamsSelectionDeletable(list, selected) {
  if (!Array.isArray(selected) || !selected.length) return false;
  const rows = Array.isArray(list) ? list : [];
  return selected.every((sel) => {
    const id = sel && typeof sel === "object" ? sel.id || sel.messageRequestId : sel;
    const row = rows.find((m) => m && ((m.id != null && String(m.id) === String(id)) || (m.messageRequestId != null && String(m.messageRequestId) === String(id))))
      || (sel && typeof sel === "object" ? sel : null);
    if (!row || row.isDeleted) return false;
    const caps = teamsRowCaps(row);
    if (caps) return caps.canDelete;
    return !!row.local;
  });
}

/** The row fields a teamsDeliveryState / teamsDeliveryFailed socket event sets (null: not for this row). */
export function teamsDeliveryPatch(row, event, failed) {
  if (!row || !event) return null;
  const teams = { ...(row.teams && typeof row.teams === "object" ? row.teams : {}) };
  if (failed) {
    teams.sendState = "failed";
    return {
      isTeams: true,
      teams,
      teamsDeliveryFailed: true,
      teamsDeliveryCode: event.code || null,
      teamsDeliveryMessage: event.message || null,
    };
  }
  teams.sendState = event.sendState || "sent";
  if (event.graphMessageId) teams.graphMessageId = event.graphMessageId;
  return { isTeams: true, teams, teamsDeliveryFailed: false, teamsDeliveryCode: null, teamsDeliveryMessage: null };
}

/**
 * Applies a teamsDeliveryState / teamsDeliveryFailed event to a message list. Matches by server id
 * (or `replacedByChatId`) and, for an own media row not merged with its server id yet, by
 * messageRequestId (that row then takes the server id, so its retry goes to the right message).
 * Returns the same array when nothing changed.
 */
export function applyTeamsDelivery(list, event, failed) {
  if (!Array.isArray(list) || !event || !event.chatId) return list;
  const ids = [event.chatId, event.replacedByChatId].filter(Boolean).map(String);
  const reqId = event.messageRequestId ? String(event.messageRequestId) : null;
  const byReqId = (m) => reqId != null && m.messageRequestId != null && String(m.messageRequestId) === reqId;
  const localId = (m) => m.id == null || String(m.id) === String(m.messageRequestId);
  let changed = false;
  const next = list.map((m) => {
    if (!m || !((m.id != null && ids.includes(String(m.id))) || byReqId(m))) return m;
    const patch = teamsDeliveryPatch(m, event, failed);
    if (!patch) return m;
    changed = true;
    return { ...m, ...patch, ...(byReqId(m) && localId(m) ? { id: event.chatId } : null) };
  });
  return changed ? next : list;
}

/** Fields of an inbound Microsoft Teams `edited` event beyond the text (a part's file can change). */
export function teamsEditedFields(data) {
  const out = {};
  if (!data || data.source !== "teams") return out;
  ["messageType", "fileUrl", "attachedFileName", "mediaAlbumId"].forEach((k) => {
    if (data[k] !== undefined && data[k] !== null) out[k] = data[k];
  });
  return out;
}

/** Initials of a Teams person for the avatar (no Teams photo in Kjuup); '?' when the name is blank. */
export const teamsInitials = (name) => coreTeamsInitials(name, "?");

/** "Anna Berg, Tim Koch" for the header subtitle: the Teams people (and Kjuup members) except me. */
export function teamsMembersLine(users, myId) {
  if (!Array.isArray(users)) return "";
  return users
    .filter((u) => u && !u.isLeave && (myId == null || String(u.id) !== String(myId)))
    .map((u) => String(u.fullName || u.name || [u.firstName, u.lastName].filter(Boolean).join(" ") || "").trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * The sender name of a message in a Teams chat: a Teams person carries it on the row
 * (senderName), a Kjuup member (the owner) is looked up in the chat's users.
 */
export function teamsSenderName(row, users, fallback = "Teams user") {
  if (!row) return fallback;
  const own = typeof row.senderName === "string" ? row.senderName.trim() : "";
  if (own) return own;
  const u = Array.isArray(users) ? users.find((x) => x && row.senderId != null && String(x.id) === String(row.senderId)) : null;
  const name = u ? String(u.fullName || [u.firstName, u.lastName].filter(Boolean).join(" ") || "").trim() : "";
  return name || fallback;
}

/** Mention suggestions for a Teams chat: the members as they are (no "@All", Teams has no such mention). */
export const teamsMentionSuggestions = (members) =>
  (Array.isArray(members) ? members : []).filter((m) => m && m.type !== "All" && m.id !== "id-all12");

// ---- files -------------------------------------------------------------------------------------

const SIGNED_RE = /&exp=\d+&sig=[A-Za-z0-9_-]+/;

/** True for a signed Teams file path ('<path>&exp=..&sig=..'), with or without the getFile host. */
export const isSignedFileUrl = (url) => typeof url === "string" && SIGNED_RE.test(url);

/** The path without its '&exp=..&sig=..' signature (and anything after it). */
export const stripFileSignature = (url) => (typeof url === "string" ? url.replace(/&exp=\d+&sig=.*$/, "") : url);

/** Extension of a file name ('Plan v2.PDF' → 'pdf'), '' when it has none. */
export function extensionOfName(name) {
  const base = String(name || "").split(/[\\/]/).pop();
  const at = base.lastIndexOf(".");
  if (at <= 0 || at === base.length - 1) return "";
  return base.slice(at + 1).toLowerCase();
}

/** File extension for saving / naming: attachedFileName first; the URL only for unsigned paths. */
export function fileExtensionFor(row, url, fallback = "") {
  const fromName = extensionOfName(row && row.attachedFileName);
  if (fromName) return fromName;
  if (!url || isSignedFileUrl(url) || isTeamsRow(row)) return fallback;
  const clean = String(url).split(/[#?]/)[0];
  return extensionOfName(clean) || fallback;
}

/**
 * What a failed /getFile of a signed Teams path means: 'reload' (403 `expired` only: refetch the page
 * for fresh URLs), 'openInTeams' (404: the file is only available in Teams), 'retry' (409 / 5xx:
 * transient) or null. Another 403 (`bad_signature`, `unsigned`, `signing_unavailable`) is not fixed
 * by a refetch, so it does nothing.
 */
export function teamsFileErrorAction(status, body) {
  const code = body && typeof body === "object" ? body.code : null;
  if (code === "expired") return "reload";
  if (status === 403 || code === "bad_signature" || code === "unsigned" || code === "signing_unavailable") return null;
  if (status === 404 || code === "teams_file_unavailable" || code === "unsupported_source" || (body && body.openInTeams)) return "openInTeams";
  if (status === 409 || status === 502 || status === 503 || status === 500) return "retry";
  return null;
}

// Probes of failed signed files: one per path a minute, and at most PROBE_BURST in PROBE_WINDOW_MS,
// so a pane of broken thumbnails does not fire a request per row per render.
const PROBE_PER_URL_MS = 60000;
const PROBE_WINDOW_MS = 10000;
const PROBE_BURST = 3;
const PROBE_MEMORY = 200;
const probedAt = new Map();
let probeWindow = [];

/** True when a probe of `url` may run now (and records it). */
export function takeTeamsFileProbe(url, now = Date.now()) {
  const key = stripFileSignature(url);
  const last = probedAt.get(key);
  if (last != null && now - last < PROBE_PER_URL_MS) return false;
  probeWindow = probeWindow.filter((t) => now - t < PROBE_WINDOW_MS);
  if (probeWindow.length >= PROBE_BURST) return false;
  probeWindow.push(now);
  probedAt.delete(key);
  probedAt.set(key, now);
  if (probedAt.size > PROBE_MEMORY) probedAt.delete(probedAt.keys().next().value);
  return true;
}

/** Test hook: forget the probe throttle. */
export function resetTeamsFileProbes() {
  probedAt.clear();
  probeWindow = [];
}

/**
 * A signed Teams file did not load (image onError, download failure): ask the server why (only for
 * signed paths, so native media never pays for it; throttled by takeTeamsFileProbe) and tell the open
 * chat (TEAMS_FILE_ERROR_EVENT). `status` / `body` skip the probe when the caller already has them.
 */
export async function reportTeamsFileError(url, status, body, { now = Date.now, fetchImpl } = {}) {
  if (!isSignedFileUrl(url)) return null;
  let st = status;
  let data = body;
  if (st == null) {
    if (!takeTeamsFileProbe(url, now())) return null;
    const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
    if (!doFetch) return null;
    try {
      const res = await doFetch(url, { method: "GET", headers: { Accept: "application/json" } });
      st = res.status;
      if (st >= 400) {
        try {
          data = await res.json();
        } catch (_) {
          data = null;
        }
      }
    } catch (_) {
      return null;
    }
  }
  const action = teamsFileErrorAction(st, data);
  if (action && typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    try {
      window.dispatchEvent(new CustomEvent(TEAMS_FILE_ERROR_EVENT, { detail: { action, url, status: st, code: data && data.code } }));
    } catch (_) {
      /* old browser without CustomEvent */
    }
  }
  return action;
}

// ---- refreshing expired file URLs -------------------------------------------------------------

const URL_KEYS = ["fileUrl", "thumbnailUrl"];

// The file paths of one message (fileUrl may be a string or an array; inReplyTo carries its own).
function urlsOf(m) {
  const out = [];
  if (!m || typeof m !== "object") return out;
  const add = (v) => {
    if (typeof v === "string" && v) out.push(v);
    else if (Array.isArray(v)) v.forEach(add);
  };
  URL_KEYS.forEach((k) => add(m[k]));
  if (m.inReplyTo && typeof m.inReplyTo === "object") URL_KEYS.forEach((k) => add(m.inReplyTo[k]));
  return out;
}

// Every message a list row stands for: the row itself, plus each image of a merged album.
function unitsOf(row) {
  if (!row || typeof row !== "object") return [];
  const imgs = Array.isArray(row.images) && row.images.length > 1 ? row.images.filter(Boolean) : null;
  return imgs ? imgs : [row];
}

const timeOf = (m) => {
  const t = new Date(m && m.createdAt).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * The history page (1-based, newest first, `pageSize` messages a page like getChat) that holds the
 * message whose file `url` failed, from the rows on screen: { id, page } or null when no loaded row
 * carries that file. The signature is ignored, so a row matches whatever `exp` it has.
 */
export function teamsPageOfFile(list, url, pageSize = 30) {
  if (!Array.isArray(list) || typeof url !== "string" || !url) return null;
  const want = stripFileSignature(url);
  // The failing url is the full /getFile link; a row holds the signed path that the link contains.
  const matches = (u) => u === url || want.endsWith(stripFileSignature(u)) || want.includes(stripFileSignature(u));
  const units = [];
  list.forEach((row) => unitsOf(row).forEach((u) => units.push(u)));
  const hit = units.find((u) => u && u.id != null && urlsOf(u).some((v) => isSignedFileUrl(v) && matches(v)));
  if (!hit) return null;
  const at = timeOf(hit);
  const seen = new Set();
  let newer = 0;
  units.forEach((u) => {
    if (!u || u === hit || u.id == null || seen.has(String(u.id))) return;
    seen.add(String(u.id));
    const t = timeOf(u);
    if (at != null && t != null && t > at) newer += 1;
  });
  return { id: hit.id, page: Math.floor(newer / Math.max(1, pageSize)) + 1 };
}

// The URL fields of a fresh copy of the message (only those it has).
function urlFieldsOf(fresh, current) {
  const out = {};
  URL_KEYS.forEach((k) => {
    if (fresh[k] !== undefined && fresh[k] !== null && fresh[k] !== current[k]) out[k] = fresh[k];
  });
  if (fresh.inReplyTo && typeof fresh.inReplyTo === "object" && current.inReplyTo && typeof current.inReplyTo === "object") {
    const r = {};
    URL_KEYS.forEach((k) => {
      if (fresh.inReplyTo[k] != null && fresh.inReplyTo[k] !== current.inReplyTo[k]) r[k] = fresh.inReplyTo[k];
    });
    if (Object.keys(r).length) out.inReplyTo = { ...current.inReplyTo, ...r };
  }
  return out;
}

/**
 * The rows on screen with the freshly signed file URLs of `freshRows` (a getChat page), by message
 * id, album images included. Returns the same array when no URL changed; nothing else on a row
 * changes.
 */
export function applyTeamsFileUrls(list, freshRows) {
  if (!Array.isArray(list) || !Array.isArray(freshRows) || !freshRows.length) return list;
  const byId = new Map();
  freshRows.forEach((f) => {
    if (f && f.id != null) byId.set(String(f.id), f);
  });
  let changed = false;
  const next = list.map((row) => {
    if (!row || row.id == null) return row;
    const fresh = byId.get(String(row.id));
    const fields = fresh ? urlFieldsOf(fresh, row) : {};
    if (Array.isArray(row.images) && row.images.length) {
      let imgChanged = false;
      const images = row.images.map((img) => {
        const f = img && img.id != null ? byId.get(String(img.id)) : null;
        if (!f) return img;
        const patch = urlFieldsOf(f, img);
        if (!Object.keys(patch).length) return img;
        imgChanged = true;
        return { ...img, ...patch };
      });
      if (imgChanged) fields.images = images;
    }
    if (!Object.keys(fields).length) return row;
    changed = true;
    return { ...row, ...fields };
  });
  return changed ? next : list;
}

/**
 * The pages to fetch, in order, to find message `id` again after its page was estimated (the page
 * itself, then the next and the previous one, since rows arrive and leave meanwhile).
 */
export const teamsFilePagesToTry = (page) => [page, page + 1, page - 1].filter((p) => p >= 1);

// ---- header ------------------------------------------------------------------------------------

/**
 * The name the chat header should switch to once Teams group info arrives (a push opened the chat
 * with the sender's name before the Messages list knew the chat), or null when it already shows it.
 */
export function teamsHeaderName(groupInfo, currentName) {
  if (!isTeamsPayload(groupInfo)) return null;
  const name = typeof groupInfo.name === "string" ? groupInfo.name.trim() : "";
  if (!name) return null;
  return name === String(currentName || "").trim() ? null : name;
}

/** Open the chat (or a message) in Microsoft Teams in a new tab. False when there is no link. */
export function openInTeams(webUrl) {
  if (!webUrl || typeof window === "undefined") return false;
  try {
    const w = window.open(webUrl, "_blank", "noopener,noreferrer");
    if (w) w.opener = null;
    return true;
  } catch (_) {
    return false;
  }
}

// ---- message menu ------------------------------------------------------------------------------

/**
 * The menu rows of a message in a Microsoft Teams chat (same row shape as
 * utils/chatActionRows.buildChatActionRows): reply (once the message reached Teams), select,
 * mark as task, copy, translate (others' text), Open in Teams, and edit / delete exactly when the
 * row's teams.canEdit / teams.canDelete allow it (no 10-minute window). Never forward, mark unread
 * or read info (Teams has no receipts). A row only appears when its handler is given.
 */
export function buildTeamsActionRows(p) {
  const { chat = {}, canWrite = true, isBookmarked = false, canBookmark = false, canCopy = false, canTranslate = false, webUrl = null, labels = {}, handlers = {} } = p || {};
  const rows = [];
  const add = (key, icon, label, onPress, extra = {}) => {
    if (typeof onPress !== "function") return;
    rows.push({ key, icon, label, onPress, danger: false, filled: false, ...extra });
  };
  const t = chat.teams && typeof chat.teams === "object" ? chat.teams : null;
  const caps = teamsRowCaps({ ...chat, isTeams: true }) || {};
  const writable = canWrite !== false;
  if (writable && caps.canReply) add("reply", "reply", labels.reply, handlers.reply);
  if (!chat.isDeleted) add("select", "check_circle", labels.select, handlers.select);
  if (canBookmark) {
    add("todo", isBookmarked ? "task_alt" : "add_task", isBookmarked ? labels.todoRemove : labels.todo, handlers.todo, { filled: !!isBookmarked });
  }
  if (canCopy) add("copy", "content_copy", labels.copy, handlers.copy);
  if (canTranslate) add("translate", "translate", labels.translate, handlers.translate);
  if (webUrl) add("openInTeams", "open_in_new", labels.openInTeams || "Open in Teams", handlers.openInTeams);
  // The server edits part 0 only (a long text split into parts: later parts say teams_edit_not_allowed).
  if (writable && t && caps.canEdit) add("edit", "edit", labels.edit, handlers.edit);
  if (writable && t && caps.canDelete) add("delete", "delete", labels.delete, handlers.delete, { danger: true });
  return rows;
}

// ---- sending -----------------------------------------------------------------------------------

/** A send / retry response that must not be retried (Teams failures carry `nonRetryable: true`). */
export const isNonRetryableResponse = (body) =>
  !!body && typeof body === "object" && body.status === false && (body.nonRetryable === true || body.retryable === false);
